import { Fragment, useEffect, useMemo, useState } from "react";
import {
  FaCalendarCheck,
  FaEnvelope,
  FaPenToSquare,
  FaPhone,
  FaRegBell
} from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import defaultAvatar from "../assets/smart_mentor.png";
import { requestNotificationPermission } from "../firebase";
import { setTheme } from "../redux/slices/themeSlice";
import Api from "../service/http";
import { logout } from "../service/authService";
import Loading from "../components/Loading";

const Pill = ({ children, theme }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${
    theme === 'dark' 
      ? 'bg-blue-900/20 text-blue-300 border-blue-800' 
      : 'bg-blue-50 text-blue-700 border-blue-100'
  }`}>
    {children}
  </span>
);

const InfoItem = ({ icon, label, value, theme }) => (
  <div className={`flex gap-4 items-center ${
    theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-100'
  } border rounded-2xl p-4 w-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group`}>
    <div className={`p-3 rounded-xl transition-all duration-300 ${
      theme === 'dark' 
        ? 'bg-slate-700 text-blue-400 group-hover:bg-blue-600 group-hover:text-white' 
        : 'bg-white text-blue-600 group-hover:bg-blue-600 group-hover:text-white shadow-sm'
    } text-xl shrink-0`}>
      {icon}
    </div>
    <div className="leading-tight overflow-hidden">
      <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${
        theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
      }`}>{label}</div>
      <div className={`font-bold truncate ${
        theme === 'dark' ? 'text-slate-200' : 'text-slate-900'
      }`}>{value ?? "N/A"}</div>
    </div>
  </div>
);

const CheckRow = ({ checked, label, onChange, theme }) => (
  <button
    type="button"
    onClick={onChange}
    className={`flex-1 flex items-center justify-center gap-3 rounded-xl px-4 py-2.5 transition-all duration-300 border-2 ${
      checked 
        ? 'border-blue-600 bg-blue-600/10 text-blue-600 shadow-sm' 
        : theme === 'dark' 
          ? 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600' 
          : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200'
    }`}
  >
    <div
      className={`h-4 w-4 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
        checked ? "bg-blue-600 border-blue-600" : "border-current"
      }`}
    >
      {checked && (
        <div className="h-1.5 w-1.5 rounded-full bg-white" />
      )}
    </div>
    <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
  </button>
);

const ThemeToggle = ({ theme, setThemeState }) => {
  return (
    <div className={`${theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white/10 border-white/20'} border backdrop-blur-md rounded-2xl p-4 transition-all duration-300 shadow-lg`}>
      <div className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-3 ${theme === 'dark' ? 'text-slate-500' : 'text-white/70'}`}>Theme Preference</div>
      <div className="flex gap-2">
        <CheckRow
          checked={theme === "light"}
          label="Light"
          onChange={() => setThemeState("light")}
          theme={theme}
        />
        <CheckRow
          checked={theme === "dark"}
          label="Dark"
          onChange={() => setThemeState("dark")}
          theme={theme}
        />
      </div>
    </div>
  )
}

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Set by the PayPal return route (?subscription=success|cancelled) so the
  // page can say what happened instead of just showing the current state.
  const [returnNotice, setReturnNotice] = useState(() => {
    const outcome = searchParams.get("subscription");
    return outcome === "success" || outcome === "cancelled" ? outcome : null;
  });
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem("user")) || null
  );
  const [subscriptions, setSubscriptions] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const activeSub = useMemo(
    () =>
      subscriptions?.find(
        (s) =>
          (s?.status || "").toString().toLowerCase() === "active" ||
          (s?.status || "").toString().toUpperCase() === "ACTIVE"
      ) || user?.active_subscription,
    [subscriptions, user]
  );

  const getProfile = async () => {
    setIsLoading(true);
    try {
      const res = await Api.get("/web/profile");
      if (res?.data?.user) {
        localStorage.setItem("user", JSON.stringify(res?.data?.user));
        setUser(res?.data?.user);
        setSubscriptions(res?.data?.subscriptions || []);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  // Drop the query param once it has been read, so a refresh or a shared link
  // does not replay the message.
  useEffect(() => {
    if (!returnNotice) return;
    const next = new URLSearchParams(searchParams);
    next.delete("subscription");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnNotice]);

  // A PayPal subscription is activated by webhook, which can land a moment
  // after the customer returns. Re-read the profile shortly after a success.
  useEffect(() => {
    if (returnNotice !== "success") return;
    const timer = setTimeout(getProfile, 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnNotice]);

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  const theme = useSelector((state) => state?.theme?.theme || "light");
  const dispatch = useDispatch();
  const setThemeState = (t) => {
    dispatch(setTheme(t));
  };

  const fmt = (d = null) =>
    d ? new Date(d).toLocaleDateString() : "N/A";

  const statusPretty = (s = null) => {
    if (!s) return "N/A";
    const lower = s.toLowerCase();
    if (lower === "active") return "ACTIVE";
    if (lower === "canceled" || lower === "cancelled") return "CANCELED";
    return s.toUpperCase();
  };

  if (isLoading && !user) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <Loading message="Loading Profile..." />
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col h-full overflow-y-auto scrollbar-hide ${theme === 'dark' ? 'bg-slate-900 text-slate-200' : 'bg-slate-50 text-slate-900'}`}>
      <div className="space-y-6 pb-10">
        {returnNotice && (
          <div
            className={`mx-4 mt-4 sm:mx-6 lg:mx-8 rounded-2xl border-2 px-5 py-4 flex items-start gap-3 ${
              returnNotice === "success"
                ? theme === "dark"
                  ? "bg-green-900/20 border-green-800 text-green-200"
                  : "bg-green-50 border-green-200 text-green-800"
                : theme === "dark"
                ? "bg-amber-900/20 border-amber-800 text-amber-200"
                : "bg-amber-50 border-amber-200 text-amber-900"
            }`}
          >
            <span className="text-lg leading-none mt-0.5">
              {returnNotice === "success" ? "\u2713" : "\u26A0"}
            </span>
            <div className="flex-1">
              <p className="font-bold text-sm">
                {returnNotice === "success"
                  ? "Payment received"
                  : "Payment cancelled"}
              </p>
              <p className="text-xs mt-1 opacity-90">
                {returnNotice === "success"
                  ? "Your subscription is being activated. This page refreshes automatically in a moment."
                  : "No payment was taken. You can start again from the pricing section whenever you are ready."}
              </p>
            </div>
            <button
              onClick={() => setReturnNotice(null)}
              className="text-lg leading-none opacity-60 hover:opacity-100"
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        {/* Modern Header */}
        <div className="relative overflow-hidden bg-blue-600 shadow-xl min-h-[180px] sm:min-h-[200px] flex items-center">
          {/* Decorative Background Elements */}
          <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-64 h-64 bg-blue-400/20 rounded-full blur-3xl animate-pulse" />
          
          <div className="relative w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" />
                  <img
                    src={user?.dp || defaultAvatar}
                    alt="Profile"
                    className={`relative w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-4 ${theme === 'dark' ? 'border-slate-800' : 'border-white'} shadow-2xl transition-transform duration-500 group-hover:scale-105`}
                  />
                  <span className={`absolute bottom-2 right-2 h-5 w-5 rounded-full bg-green-500 border-4 ${theme === 'dark' ? 'border-slate-800' : 'border-white'} shadow-lg shadow-green-500/20`} />
                </div>

                <div className="text-center sm:text-left space-y-2">
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <h1 className="text-2xl sm:text-4xl font-black text-white tracking-tight drop-shadow-sm">
                      {user?.first_name}
                    </h1>
                    <button
                      onClick={() => navigate("/profile/edit")}
                      className="group p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all duration-300 backdrop-blur-md border border-white/10 shadow-lg"
                      title="Edit Profile"
                    >
                      <FaPenToSquare className="text-lg group-hover:scale-110 transition-transform" />
                    </button>
                  </div>
                  <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                    <Pill theme="dark">Member since {fmt(user?.created_at)}</Pill>
                    {activeSub && <Pill theme="dark">Pro Member</Pill>}
                  </div>
                </div>
              </div>

              <div className="w-full md:w-auto">
                <ThemeToggle theme={theme} setThemeState={setThemeState} />
              </div>
            </div>
          </div>
        </div>

        {/* Body Content */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 sm:-mt-12 space-y-6 pb-12">
          {/* Main Info Card */}
          <div className={`${theme === 'dark' ? 'bg-slate-800/80 border-slate-700/50' : 'bg-white border-slate-100'} backdrop-blur-xl rounded-[2rem] p-6 sm:p-8 shadow-2xl border`}>
            <div className="flex items-center gap-3 mb-8">
              <div className="h-8 w-1.5 bg-blue-600 rounded-full" />
              <h2 className={`text-xl font-bold tracking-tight ${theme === 'dark' ? 'text-slate-100' : 'text-slate-900'}`}>Contact Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem icon={<FaPhone />} label="Phone Number" value={user?.phone || "Not provided"} theme={theme} />
              <InfoItem icon={<FaEnvelope />} label="Email Address" value={user?.email || "Not provided"} theme={theme} />
              <InfoItem
                icon={<FaCalendarCheck />}
                label="Subscription Start"
                value={activeSub?.current_period_start ? fmt(activeSub?.current_period_start) : "No active subscription"}
                theme={theme}
              />
              <InfoItem
                icon={<FaCalendarCheck />}
                label="Subscription Expiry"
                value={activeSub?.current_period_end ? fmt(activeSub?.current_period_end) : "N/A"}
                theme={theme}
              />
            </div>
          </div>

          {/* Subscription Card */}
          <div className={`${theme === 'dark' ? 'bg-slate-800/80 border-slate-700/50' : 'bg-white border-slate-100'} backdrop-blur-xl rounded-[2rem] p-6 sm:p-8 shadow-2xl border`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="h-8 w-1.5 bg-blue-600 rounded-full" />
                <h2 className={`text-xl font-bold tracking-tight ${theme === 'dark' ? 'text-slate-100' : 'text-slate-900'}`}>Subscription Details</h2>
              </div>
              <div className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest ${
                statusPretty(activeSub?.status) === "ACTIVE" 
                  ? "bg-green-500/10 text-green-500 border border-green-500/20"
                  : "bg-red-500/10 text-red-500 border border-red-500/20"
              }`}>
                {statusPretty(activeSub?.status)}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="space-y-1">
                <p className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>Billing Provider</p>
                <p className={`text-lg font-bold capitalize ${theme === 'dark' ? 'text-slate-200' : 'text-slate-900'}`}>{activeSub?.provider || "N/A"}</p>
              </div>
              <div className="sm:col-span-1 lg:col-span-2 space-y-1">
                <p className={`text-[10px] font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>Subscription Reference</p>
                <p className={`text-sm font-mono break-all font-medium ${theme === 'dark' ? 'text-slate-200' : 'text-slate-600'}`}>{activeSub?.provider_subscription_id || "N/A"}</p>
              </div>
            </div>

            {/* Expandable full history/table (your original logic) */}
            {subscriptions?.length > 0 && (
              <div className="mt-10">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-1 w-4 bg-blue-600 rounded-full" />
                  <div className={`text-sm font-bold uppercase tracking-wider ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                    Subscription History
                  </div>
                </div>
                <div className={`overflow-hidden rounded-2xl border ${theme === 'dark' ? 'border-slate-700' : 'border-slate-100'} shadow-sm`}>
                  <div className="overflow-x-auto">
                    <table className={`min-w-full divide-y ${theme === 'dark' ? 'divide-slate-700' : 'divide-slate-100'}`}>
                      <thead className={`${theme === 'dark' ? 'bg-slate-900/50' : 'bg-slate-50'}`}>
                        <tr>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>ID</th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Status</th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Provider</th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Period</th>
                          <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Timeline</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${theme === 'dark' ? 'divide-slate-700 bg-slate-800/30' : 'divide-slate-100 bg-white'}`}>
                        {subscriptions.map((sub, index) => {
                          const lower = (sub?.status || "").toString().toLowerCase();
                          const status = lower === "active" ? "Active" : (lower === "canceled" || lower === "cancelled" ? "Canceled" : sub?.status);
                          const statusClass = lower === "active" ? "text-green-500 bg-green-500/10" : (lower === "canceled" || lower === "cancelled" ? "text-red-500 bg-red-500/10" : "text-yellow-500 bg-yellow-500/10");

                          return (
                            <Fragment key={sub?.id}>
                              <tr 
                                className={`group cursor-pointer transition-colors ${theme === 'dark' ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'}`}
                                onClick={() => setExpandedId((id) => (id === sub?.id ? null : sub?.id))}
                              >
                                <td className="px-6 py-5 whitespace-nowrap">
                                  <div className="flex items-center gap-3">
                                    <div className={`p-1.5 rounded-md transition-transform ${expandedId === sub?.id ? 'rotate-90' : ''} ${theme === 'dark' ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" /></svg>
                                    </div>
                                    <span className={`text-sm font-mono font-medium ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>{sub?.provider_subscription_id?.slice(0, 16)}...</span>
                                  </div>
                                </td>
                                <td className="px-6 py-5 whitespace-nowrap">
                                  <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider ${statusClass}`}>
                                    {status}
                                  </span>
                                </td>
                                <td className={`px-6 py-5 whitespace-nowrap text-sm font-bold capitalize ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                                  {sub?.provider}
                                </td>
                                <td className={`px-6 py-5 whitespace-nowrap text-sm font-medium ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {sub?.period === 1 ? "Monthly" : "Yearly"}
                                </td>
                                <td className="px-6 py-5 whitespace-nowrap">
                                  <div className="flex flex-col gap-0.5">
                                    <span className={`text-xs font-bold ${theme === 'dark' ? 'text-slate-200' : 'text-slate-700'}`}>{fmt(sub?.current_period_start)}</span>
                                    <span className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>to {fmt(sub?.current_period_end)}</span>
                                  </div>
                                </td>
                              </tr>

                              {expandedId === sub?.id && (
                                <tr>
                                  <td colSpan={5} className={`px-6 py-6 ${theme === 'dark' ? 'bg-slate-900/30' : 'bg-slate-50/50'}`}>
                                    <div className="space-y-4">
                                      <div className="flex items-center gap-2">
                                        <div className="w-1 h-1 bg-blue-500 rounded-full" />
                                        <h4 className={`text-xs font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'}`}>Payment History</h4>
                                      </div>
                                      {sub?.subscription_histories?.length > 0 ? (
                                        <div className={`overflow-hidden rounded-xl border ${theme === 'dark' ? 'border-slate-700/50' : 'border-slate-200'} bg-white/5`}>
                                          <table className="min-w-full divide-y divide-slate-700/10">
                                            <thead>
                                              <tr className={theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-100/50'}>
                                                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-tighter text-slate-500">Billed At</th>
                                                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-tighter text-slate-500">Amount</th>
                                                <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-tighter text-slate-500">Action</th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-700/10">
                                              {sub?.subscription_histories?.map((h) => (
                                                <tr key={h?.id}>
                                                  <td className={`px-4 py-4 text-sm font-medium ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>{fmt(h?.billed_at)}</td>
                                                  <td className={`px-4 py-4 text-sm font-bold ${theme === 'dark' ? 'text-slate-100' : 'text-slate-900'}`}>${h?.amount}</td>
                                                  <td className="px-4 py-4">
                                                    {h?.data?.data?.object?.invoice_pdf ? (
                                                      <a
                                                        href={h?.data.data.object.invoice_pdf}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-2 text-xs font-bold text-blue-500 hover:text-blue-600 transition-colors bg-blue-500/10 px-3 py-1.5 rounded-lg"
                                                      >
                                                        <span>DOWNLOAD</span>
                                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                                      </a>
                                                    ) : (
                                                      <span className="text-xs text-slate-400">N/A</span>
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : (
                                        <div className={`text-center py-6 rounded-xl border-2 border-dashed ${theme === 'dark' ? 'border-slate-700 text-slate-500' : 'border-slate-100 text-slate-400'} text-[10px] font-bold uppercase tracking-widest`}>
                                          No transactions found
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* About / extra contact block */}
            <div className={`${theme === 'dark' ? 'bg-slate-800/80 border-slate-700/50' : 'bg-white border-slate-100'} backdrop-blur-xl rounded-[2rem] p-6 sm:p-8 shadow-2xl border flex flex-col`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-6 w-1.5 bg-blue-600 rounded-full" />
                <h2 className={`text-lg font-bold tracking-tight ${theme === 'dark' ? 'text-slate-100' : 'text-slate-900'}`}>About Me</h2>
              </div>
              <p className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                {user?.about || "No bio information provided yet. Click the edit button above to tell us about yourself."}
              </p>
            </div>

            {/* Notification toggles */}
            <div className={`${theme === 'dark' ? 'bg-slate-800/80 border-slate-700/50' : 'bg-white border-slate-100'} backdrop-blur-xl rounded-[2rem] p-6 sm:p-8 shadow-2xl border flex flex-col`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-6 w-1.5 bg-blue-600 rounded-full" />
                <h2 className={`text-lg font-bold tracking-tight ${theme === 'dark' ? 'text-slate-100' : 'text-slate-900'}`}>Notifications</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className={`p-4 rounded-2xl border ${user?.push_notification ? 'bg-blue-600/10 border-blue-500/20 text-blue-500' : 'bg-slate-500/5 border-transparent text-slate-500 opacity-60'}`}>
                  <div className="flex flex-col items-center gap-2">
                    <FaRegBell className="text-xl" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Push</span>
                  </div>
                </div>
                <div className={`p-4 rounded-2xl border ${user?.email_notification ? 'bg-blue-600/10 border-blue-500/20 text-blue-500' : 'bg-slate-500/5 border-transparent text-slate-500 opacity-60'}`}>
                  <div className="flex flex-col items-center gap-2">
                    <FaEnvelope className="text-xl" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Email</span>
                  </div>
                </div>
              </div>
              <p className={`mt-6 text-[10px] font-medium leading-relaxed ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'} flex items-start gap-2 italic`}>
                <span className="mt-1 shrink-0"><FaRegBell /></span>
                Note: Notification delivery also depends on your system and browser settings.
              </p>
            </div>
          </div>

          {/* Not subscribed banner (like your original) */}
          {!(user?.expired_at && new Date(user.expired_at) > new Date()) && (
            <div className={`relative overflow-hidden rounded-[2rem] p-8 border-2 border-dashed ${theme === 'dark' ? 'bg-amber-950/20 border-amber-900/50' : 'bg-amber-50 border-amber-200'}`}>
              <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-2">
                  <h3 className={`text-xl font-black tracking-tight ${theme === 'dark' ? 'text-amber-200' : 'text-amber-900'}`}>
                    Unlock Premium Features
                  </h3>
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-amber-400/80' : 'text-amber-700'}`}>
                    You're currently on the free tier. Join our community of professional traders today.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/payment")}
                  className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-black text-xs uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-amber-500/20 transition-all duration-300 text-center"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          )}

          {/* Logout button like the app */}
          <div className="flex justify-center pt-6">
            <button
              onClick={logout}
              className="group relative px-12 py-4 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black text-xs uppercase tracking-[0.3em] shadow-2xl hover:scale-105 active:scale-95 transition-all duration-300"
            >
              <span className="relative z-10">Sign Out</span>
              <div className="absolute inset-0 bg-red-600 rounded-2xl scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
